<template>
  <div class="PlanRouteView">
    <aside class="PlanRouteView__panel">
      <section class="PlanRouteView__section">
        <h1 class="title is-5">
          {{ isEditMode ? 'Edytuj trasę' : 'Zaplanuj trasę' }}
        </h1>

        <b-message
            v-if="loadError"
            type="is-danger"
            size="is-small"
        >
          {{ loadError }}
        </b-message>

        <b-message
            v-if="successMessage"
            type="is-success"
            size="is-small"
        >
          {{ successMessage }}
        </b-message>

        <b-message
            v-if="conflictMessage"
            type="is-warning"
            size="is-small"
        >
          {{ conflictMessage }}
        </b-message>

        <div class="PlanRouteView__stats">
          <span class="tag">
            Widoczne punkty stałe: {{ permanentPoints.length }}
          </span>

          <span class="tag">
            Widoczne zamówienia: {{ orderPoints.length }}
          </span>

          <span class="tag is-light">
            Bez współrzędnych: {{ pointsWithoutCoordinates.length }}
          </span>
        </div>

        <div class="PlanRouteView__loaders">
          <span v-if="isOrdersLoading">
            <i class="mdi">hourglass_empty</i>
            Ładowanie zamówień
          </span>

          <span v-if="isPermanentPointsLoading">
            <i class="mdi">hourglass_empty</i>
            Ładowanie punktów stałych
          </span>

          <span v-if="isUsersLoading">
            <i class="mdi">hourglass_empty</i>
            Ładowanie użytkowników
          </span>

          <span v-if="isRouteEditLoading">
            <i class="mdi">hourglass_empty</i>
            Ładowanie trasy do edycji
          </span>
        </div>
      </section>

      <section class="PlanRouteView__section">
        <selected-route-points
            :points="selectedPoints"
            :conflicts="conflicts"
            @input="selectedPoints = $event"
            @remove="removePoint"
        />

        <button
            type="button"
            class="button is-small is-light PlanRouteView__clear"
            :disabled="!selectedPoints.length || isSaving"
            @click="clearSelectedPoints"
        >
          Wyczyść wybór
        </button>
      </section>

      <section class="PlanRouteView__section">
        <plan-route-form
            v-model="routeForm"
            :users="users"
            :is-saving="isSaving"
            :is-users-loading="isUsersLoading"
            :errors="validationErrors"
            :form-error="formError"
            :is-edit-mode="isEditMode"
            @submit="submitRoute"
        />
      </section>

      <section
          v-if="pointsWithoutCoordinates.length"
          class="PlanRouteView__section"
      >
        <h2 class="title is-6">
          Punkty bez współrzędnych
        </h2>

        <ul class="PlanRouteView__missing">
          <li
              v-for="point in pointsWithoutCoordinates.slice(0, 8)"
              :key="point.key"
          >
            {{ point.name }}

            <small>
              {{ point.address }}
            </small>
          </li>
        </ul>
      </section>

      <Footer/>
    </aside>

    <div class="PlanRouteView__map">
      <RouteMap
          mode="planning"
          :planning-points="visibleMapPoints"
          :selected-point-keys="selectedPointKeys"
          :planning-route-color="routeForm.color"
          @toggle-point="togglePoint"
      />
    </div>
  </div>
</template>

<script>
import RouteMap from '@/components/Map'
import Footer from '@/components/Footer'
import SelectedRoutePoints from '@/components/planning/SelectedRoutePoints'
import PlanRouteForm from '@/components/planning/PlanRouteForm'

import OrderService from '@/services/OrderService'
import PermanentPointService from '@/services/PermanentPointService'
import RouteService from '@/services/RouteService'
import UserService from '@/services/UserService'

import {
  normalizeOrder,
  normalizePermanentPoint,
  normalizeCollection,
  pointToPlanPayload
} from '@/adapters/MapPointAdapter'

import {
  DEFAULT_ROUTE_COLOR
} from '@/configs/routeColors'

const defaultRouteName = () => {
  const now = new Date()
  const day = String(now.getDate()).padStart(2, '0')
  const month = String(now.getMonth() + 1).padStart(2, '0')

  return `Trasa ${day}.${month}.${now.getFullYear()}`
}

export default {
  name: 'PlanRouteView',

  components: {
    Footer,
    RouteMap,
    SelectedRoutePoints,
    PlanRouteForm
  },

  data() {
    return {
      orderPoints: [],
      permanentPoints: [],
      users: [],
      selectedPoints: [],

      routeForm: {
        name: defaultRouteName(),
        user: null,
        color: DEFAULT_ROUTE_COLOR
      },

      isOrdersLoading: false,
      isPermanentPointsLoading: false,
      isUsersLoading: false,
      isSaving: false,
      isEditMode: false,
      isRouteEditLoading: false,

      editingRouteId: null,
      editingRouteStatus: null,

      routeDataLoaded: false,
      routeLoadToken: 0,

      loadError: '',
      formError: '',
      successMessage: '',
      conflictMessage: '',

      conflicts: [],
      validationErrors: []
    }
  },

  computed: {
    allPoints() {
      return [
        ...this.orderPoints,
        ...this.permanentPoints
      ]
    },

    visibleMapPoints() {
      return this.allPoints.filter(
          point => point.hasCoordinates
      )
    },

    pointsWithoutCoordinates() {
      return this.orderPoints.filter(
          point => !point.hasCoordinates
      )
    },

    selectedPointKeys() {
      return this.selectedPoints.map(
          point => point.key
      )
    },

    routeIdToEdit() {
      return (
          this.$route &&
          this.$route.params &&
          this.$route.params.routeId
      ) || null
    }
  },

  watch: {
    routeIdToEdit(newRouteId, previousRouteId) {
      if (!this.routeDataLoaded) {
        return
      }

      if (
          String(newRouteId || '') ===
          String(previousRouteId || '')
      ) {
        return
      }

      this.synchronizeRouteMode(newRouteId)
    }
  },

  methods: {
    createDefaultRouteForm() {
      return {
        name: defaultRouteName(),
        user: null,
        color: DEFAULT_ROUTE_COLOR
      }
    },

    resetPlanningMode() {
      /*
       * Unieważniamy aktualnie wykonywane pobranie trasy.
       * Opóźniona odpowiedź API nie będzie mogła ponownie
       * uruchomić trybu edycji po kliknięciu „Planuj trasę”.
       */
      this.routeLoadToken += 1

      this.isEditMode = false
      this.isRouteEditLoading = false

      this.editingRouteId = null
      this.editingRouteStatus = null

      this.selectedPoints = []
      this.routeForm = this.createDefaultRouteForm()

      this.loadError = ''
      this.formError = ''
      this.successMessage = ''
      this.conflictMessage = ''

      this.conflicts = []
      this.validationErrors = []
    },

    async synchronizeRouteMode(routeId) {
      const normalizedRouteId = routeId
          ? String(routeId)
          : ''

      if (!normalizedRouteId) {
        this.resetPlanningMode()

        try {
          await this.loadOrders()
        } catch (error) {
          this.loadError = this.formatError(
              error,
              'Nie udało się odświeżyć listy zamówień.'
          )
        }

        return
      }

      this.resetPlanningMode()

      await this.loadRouteForEdit(normalizedRouteId)
    },

    loadAll() {
      this.loadError = ''

      return Promise.all([
        this.loadOrders(),
        this.loadUsers(),
        this.loadPermanentPoints()
      ]).catch(error => {
        this.loadError = this.formatError(
            error,
            'Nie udało się pobrać danych planowania.'
        )
      })
    },

    loadOrders() {
      this.isOrdersLoading = true

      return OrderService.getAll({
        sorting: {
          field: 'id',
          order: 'desc'
        },
        perPage: 500,
        pageNr: 1,
        filter: {
          by: 'status',
          value: 'new'
        },
        searchText: '',
        where: [
          {
            whereField: 'route[exists]',
            whereValue: false
          }
        ]
      })
          .then(response => {
            this.orderPoints = normalizeCollection(response.data)
                .map(normalizeOrder)
                .filter(point => point && point.id)

            this.dropUnavailableSelectedOrders()
          })
          .finally(() => {
            this.isOrdersLoading = false
          })
    },

    loadPermanentPoints() {
      this.isPermanentPointsLoading = true

      return PermanentPointService.getAll(
          {
            sorting: {
              field: 'id',
              order: 'asc'
            }
          },
          500,
          1
      )
          .then(response => {
            this.permanentPoints = normalizeCollection(response.data)
                .map(normalizePermanentPoint)
                .filter(point => point && point.id)
          })
          .finally(() => {
            this.isPermanentPointsLoading = false
          })
    },

    loadUsers() {
      this.isUsersLoading = true

      return UserService.getAll(
          {
            field: 'id',
            order: 'asc'
          },
          500,
          1
      )
          .then(response => {
            this.users = normalizeCollection(response.data)
                .filter(user => user && user.id)
          })
          .finally(() => {
            this.isUsersLoading = false
          })
    },

    getPointType(point) {
      return point && point.isPermanent
          ? 'permanentPoint'
          : 'order'
    },

    isSamePoint(firstPoint, secondPoint) {
      if (!firstPoint || !secondPoint) {
        return false
      }

      return (
          this.getPointType(firstPoint) ===
          this.getPointType(secondPoint) &&
          Number(firstPoint.id) === Number(secondPoint.id)
      )
    },

    togglePoint(point) {
      this.clearMessages()

      const index = this.selectedPoints.findIndex(
          selectedPoint => this.isSamePoint(
              selectedPoint,
              point
          )
      )

      if (index >= 0) {
        this.selectedPoints.splice(index, 1)

        return
      }

      this.selectedPoints.push(point)
    },

    removePoint(point) {
      this.selectedPoints = this.selectedPoints.filter(
          selectedPoint => !this.isSamePoint(
              selectedPoint,
              point
          )
      )
    },

    clearSelectedPoints() {
      this.selectedPoints = []
      this.conflicts = []
      this.conflictMessage = ''
    },

    dropUnavailableSelectedOrders() {
      if (this.isEditMode) {
        return
      }

      const availableOrderIds = new Set(
          this.orderPoints.map(
              point => Number(point.id)
          )
      )

      this.selectedPoints = this.selectedPoints.filter(
          point => (
              point.isPermanent ||
              availableOrderIds.has(Number(point.id))
          )
      )
    },

    clearMessages() {
      this.formError = ''
      this.successMessage = ''
      this.conflictMessage = ''
      this.validationErrors = []
      this.conflicts = []
    },

    validateBeforeSubmit() {
      const errors = []

      if (
          !this.routeForm.name ||
          !this.routeForm.name.trim()
      ) {
        errors.push({
          field: 'name',
          message: 'Podaj nazwę trasy.'
        })
      }

      if (
          this.routeForm.color &&
          !/^#[0-9a-fA-F]{6}$/.test(this.routeForm.color)
      ) {
        errors.push({
          field: 'color',
          message: 'Kolor musi mieć format #RRGGBB.'
        })
      }

      if (!this.selectedPoints.length) {
        this.formError =
            'Wybierz co najmniej jeden punkt trasy.'
      }

      this.validationErrors = errors

      return (
          errors.length === 0 &&
          this.selectedPoints.length > 0
      )
    },

    buildPayload() {
      const payload = {
        name: this.routeForm.name.trim(),
        status: this.isEditMode
            ? this.editingRouteStatus || 'planned'
            : 'planned',
        points: this.selectedPoints.map(
            pointToPlanPayload
        )
      }

      if (this.routeForm.user) {
        payload.user = this.routeForm.user
      }

      if (this.routeForm.color) {
        payload.color = this.routeForm.color
      }

      return payload
    },

    submitRoute() {
      if (this.isSaving) {
        return
      }

      this.clearMessages()

      if (!this.validateBeforeSubmit()) {
        return
      }

      this.isSaving = true

      const savePromise = this.isEditMode
          ? RouteService.updatePlan(
              this.editingRouteId,
              this.buildPayload()
          )
          : RouteService.planRoute(
              this.buildPayload()
          )

      savePromise
          .then(response => {
            this.successMessage = this.isEditMode
                ? `Trasa "${response.data.name}" została zaktualizowana.`
                : `Trasa "${response.data.name}" została utworzona.`

            this.$snackbar.open(
                this.successMessage
            )

            if (!this.isEditMode) {
              this.selectedPoints = []
            }

            this.conflicts = []
            this.conflictMessage = ''

            return this.loadOrders()
          })
          .catch(error => {
            return this.handleSaveError(error)
          })
          .finally(() => {
            this.isSaving = false
          })
    },

    resolveUserIri(user) {
      if (!user) {
        return null
      }

      if (typeof user === 'string') {
        return user
      }

      return (
          user['@id'] ||
          (
              user.id
                  ? `/api/users/${user.id}`
                  : null
          )
      )
    },

    extractRoute(data) {
      if (!data) {
        return null
      }

      if (
          typeof data === 'object' &&
          !Array.isArray(data) &&
          data.id != null
      ) {
        return data
      }

      return normalizeCollection(data)[0] || null
    },

    buildSelectedPointsFromRoute(route) {
      if (
          !route ||
          !Array.isArray(route.points)
      ) {
        return []
      }

      return route.points
          .map(entry => {
            if (!entry || !entry.point) {
              return null
            }

            if (entry.type === 'permanent_point') {
              const point = normalizePermanentPoint({
                ...entry.point,
                description:
                    entry.description ||
                    entry.point.description ||
                    ''
              })

              return point && point.id
                  ? point
                  : null
            }

            const point = normalizeOrder(entry.point)

            return point && point.id
                ? point
                : null
          })
          .filter(Boolean)
    },

    async loadRouteForEdit(routeId) {
      const normalizedRouteId = String(routeId || '')

      if (!normalizedRouteId) {
        this.resetPlanningMode()

        return
      }

      const currentLoadToken = ++this.routeLoadToken

      this.isRouteEditLoading = true
      this.loadError = ''

      let route = null

      try {
        const response = await RouteService.find(
            normalizedRouteId
        )

        /*
         * Użytkownik mógł w czasie pobierania kliknąć
         * „Planuj trasę” albo otworzyć inną trasę.
         */
        if (
            currentLoadToken !== this.routeLoadToken ||
            String(this.routeIdToEdit || '') !== normalizedRouteId
        ) {
          return
        }

        route = this.extractRoute(response.data)
      } catch (error) {
        if (currentLoadToken !== this.routeLoadToken) {
          return
        }

        this.loadError = this.formatError(
            error,
            'Nie udało się wczytać trasy do edycji.'
        )

        this.isRouteEditLoading = false

        return
      }

      if (
          currentLoadToken !== this.routeLoadToken ||
          String(this.routeIdToEdit || '') !== normalizedRouteId
      ) {
        return
      }

      if (!route) {
        this.loadError =
            'Nie znaleziono trasy do edycji.'

        this.isRouteEditLoading = false

        return
      }

      this.editingRouteId = route.id
      this.editingRouteStatus =
          route.status || 'planned'
      this.isEditMode = true

      this.routeForm = {
        name:
            route.name ||
            defaultRouteName(),
        user: this.resolveUserIri(
            route.user ||
            route.creator
        ),
        color:
            route.color ||
            DEFAULT_ROUTE_COLOR
      }

      this.selectedPoints =
          this.buildSelectedPointsFromRoute(route)

      const orderIds = this.selectedPoints
          .filter(point => !point.isPermanent)
          .map(point => point.id)

      try {
        await this.loadMissingRouteOrders(
            orderIds
        )
      } catch (error) {
        console.error(
            'Nie udało się uzupełnić pełnych danych zamówień trasy.',
            error
        )
      } finally {
        if (currentLoadToken === this.routeLoadToken) {
          this.isRouteEditLoading = false
        }
      }
    },

    loadMissingRouteOrders(orderIds) {
      const normalizedIds = Array.from(
          new Set(
              (orderIds || [])
                  .map(id => Number(id))
                  .filter(
                      id =>
                          Number.isInteger(id) &&
                          id > 0
                  )
          )
      )

      if (!normalizedIds.length) {
        return Promise.resolve()
      }

      /*
       * Jeżeli OrderService posiada getByIds(), używamy
       * endpointów orders/{id}, które zwracają pełne adresy.
       */
      if (typeof OrderService.getByIds === 'function') {
        return OrderService.getByIds(normalizedIds)
            .then(result => {
              const orders = Array.isArray(result)
                  ? result
                  : (
                      result &&
                      Array.isArray(result.data)
                          ? result.data
                          : []
                  )

              this.mergeFullRouteOrders(orders)
            })
      }

      /*
       * Zgodność wsteczna, gdy getByIds() nie zostało jeszcze
       * dodane do OrderService.
       */
      return Promise.all(
          normalizedIds.map(id =>
              OrderService.find(id)
                  .then(response => response.data)
                  .catch(error => {
                    console.warn(
                        `Nie udało się pobrać zamówienia ${id}.`,
                        error
                    )

                    return null
                  })
          )
      ).then(orders => {
        this.mergeFullRouteOrders(
            orders.filter(Boolean)
        )
      })
    },

    mergeFullRouteOrders(orders) {
      const fullOrderPoints = (orders || [])
          .map(normalizeOrder)
          .filter(point => point && point.id)

      if (!fullOrderPoints.length) {
        return
      }

      const existingPoolIds = new Set(
          this.orderPoints.map(
              point => Number(point.id)
          )
      )

      const newPoolPoints = fullOrderPoints.filter(
          point => !existingPoolIds.has(
              Number(point.id)
          )
      )

      if (newPoolPoints.length) {
        this.orderPoints = [
          ...this.orderPoints,
          ...newPoolPoints
        ]
      }

      /*
       * Używamy globalThis.Map, ponieważ komponent mapy
       * jest importowany jako RouteMap. Chroni to także
       * przed wcześniejszym konfliktem nazwy Map.
       */
      const fullById = new globalThis.Map(
          fullOrderPoints.map(point => [
            Number(point.id),
            point
          ])
      )

      this.selectedPoints = this.selectedPoints.map(
          point => {
            if (point.isPermanent) {
              return point
            }

            const fullPoint = fullById.get(
                Number(point.id)
            )

            if (!fullPoint) {
              return point
            }

            return {
              ...point,
              ...fullPoint,
              key: point.key || fullPoint.key
            }
          }
      )
    },

    handleSaveError(error) {
      if (error && error.status === 409) {
        this.conflicts =
            (
                error.data &&
                error.data.conflicts
            ) || []

        this.conflictMessage =
            'Część punktów nie jest już dostępna. Lista zamówień została odświeżona.'

        this.$snackbar.open({
          message: this.conflictMessage,
          type: 'is-warning'
        })

        return this.loadOrders()
      }

      if (error && error.status === 422) {
        this.validationErrors =
            (
                error.data &&
                error.data.errors
            ) || []

        this.formError =
            (
                error.data &&
                error.data.message
            ) ||
            'Dane trasy są niepoprawne.'

        return
      }

      if (error && error.status === 403) {
        this.formError =
            'Nie masz uprawnień do zapisania tej trasy.'

        return
      }

      if (error && error.status === 404) {
        this.formError =
            (
                error.data &&
                error.data.message
            ) ||
            'Nie znaleziono jednego z punktów.'

        return
      }

      this.formError = this.formatError(
          error,
          this.isEditMode
              ? 'Nie udało się zaktualizować trasy.'
              : 'Nie udało się utworzyć trasy.'
      )
    },

    formatError(error, fallback) {
      if (!error) {
        return fallback
      }

      if (error.name === 'NetworkError') {
        return 'Błąd sieci. Sprawdź połączenie z API.'
      }

      if (
          error.data &&
          error.data.message
      ) {
        return error.data.message
      }

      if (
          error.data &&
          error.data.detail
      ) {
        return error.data.detail
      }

      return fallback
    },

    goToOrdersTab(tabName) {
      this.$store.dispatch(
          'orders/selectTab',
          tabName
      )

      this.$router.push({
        name: 'orders'
      })
    }
  },

  created() {
    this.loadAll()
        .then(() => {
          this.routeDataLoaded = true

          return this.synchronizeRouteMode(
              this.routeIdToEdit
          )
        })
        .catch(error => {
          this.routeDataLoaded = true

          this.loadError = this.formatError(
              error,
              'Nie udało się przygotować widoku planowania trasy.'
          )
        })
  }
}
</script>

<style lang="scss" scoped>
.PlanRouteView {
  display: grid;
  grid-template-columns: minmax(360px, 40%) minmax(0, 1fr);
  height: 100%;
  min-height: 0;

  @media (max-width: 1100px) {
    display: flex;
    flex-direction: column;
    overflow: auto;
  }

  &__map {
    min-height: 0;
    border-left: 3px solid green;
    background-color: rgb(223, 153, 153);

    @media (max-width: 1100px) {
      height: 520px;
      min-height: 520px;
    }
  }

  &__panel {
    overflow: auto;
    padding: 12px;
    background: #fff;
  }

  &__section {
    margin-bottom: 18px;
  }

  &__tabs {
    margin-bottom: 12px;

    ul {
      flex-wrap: wrap;
    }

    a {
      padding: 7px 10px;
      font-size: 13px;
    }
  }

  &__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
  }

  &__loaders {
    display: grid;
    gap: 4px;
    color: #777;
    font-size: 12px;
  }

  &__clear {
    margin-top: 10px;
  }

  &__missing {
    margin-left: 0;
    list-style: none;
    font-size: 12px;

    li {
      padding: 4px 0;
      border-bottom: 1px solid #eee;
    }

    small {
      display: block;
      color: #777;
    }
  }
}
</style>